<template>
  <div class="max-w-[1000px] mx-auto py-5 animate-fade-in">
    <PageHeader title="邀请码管理" subtitle="创建并管理用于限制新用户注册的邀请码">
      <template #icon><Ticket /></template>
      <template #actions>
        <el-button :icon="Refresh" @click="loadInvites" plain class="hover-lift">刷新</el-button>
        <el-button type="primary" :icon="Plus" @click="showInviteDialog" class="hover-lift"
          >创建邀请码</el-button
        >
      </template>
    </PageHeader>

    <UiCard shadow="never">
      <el-table :data="invites" class="modern-table w-full">
        <el-table-column prop="code" label="邀请码" min-width="180">
          <template #default="{ row }">
            <el-text copyable class="code-text">{{ row.code }}</el-text>
          </template>
        </el-table-column>
        <el-table-column label="可用次数" width="120" align="center">
          <template #default="{ row }">
            <div
              class="usage-tag"
              :style="{ backgroundColor: getRemainingBg(row), color: getRemainingColor(row) }"
            >
              {{ row.used_count || 0 }} / {{ row.total_uses || '∞' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="used_by" label="最后使用者" min-width="150">
          <template #default="{ row }">
            {{ row.used_by || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="note" label="备注说明" min-width="180">
          <template #default="{ row }">
            <span class="note-text">{{ row.note || '无' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" min-width="160">
          <template #default="{ row }">
            <span class="time-text">{{ formatDate(row.created_at) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" fixed="right" align="center">
          <template #default="{ row }">
            <el-button size="small" type="danger" :icon="Delete" @click="deleteInvite(row)" link />
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <CursorPager
          v-if="invites.length > 0"
          :count="invites.length"
          :loading="pagination.isLoading.value"
          :disabled-prev="!pagination.canGoPrev.value"
          :disabled-next="!pagination.canGoNext.value"
          @prev="handlePrevPage"
          @next="handleNextPage"
        />
      </div>
    </UiCard>

    <!-- Create Invite Dialog -->
    <UiDialog v-model="inviteDialogVisible" title="创建新邀请码">
      <div class="p-6">
        <el-form label-position="top">
          <el-form-item label="生成模式">
            <UiSegmented v-model="inviteMode">
              <el-radio-button value="auto">自动随机</el-radio-button>
              <el-radio-button value="manual">手动指定</el-radio-button>
            </UiSegmented>
          </el-form-item>

          <el-form-item v-if="inviteMode === 'manual'" label="邀请码文本" required>
            <div class="w-full">
              <el-input v-model="customInviteCode" placeholder="请输入邀请码，支持任意字符" />
              <p class="mt-1.5 mb-0 text-xs text-[var(--color-text-light)]">
                支持空格、引号、斜杠、反斜杠和 Unicode 字符；提交时会安全编码，邀请码原文不会改变。
              </p>
            </div>
          </el-form-item>

          <el-form-item v-else label="随机预览">
            <div
              class="flex items-center justify-between bg-[var(--color-background-soft)] py-3 px-4 rounded-lg border border-dashed border-[var(--el-color-primary)]"
            >
              <span class="font-mono text-lg font-bold text-[var(--el-color-primary)]">{{
                previewInviteCode
              }}</span>
              <el-button link :icon="Refresh" @click="refreshPreview">换一个</el-button>
            </div>
          </el-form-item>

          <el-divider />

          <el-form-item label="使用限制">
            <el-radio-group v-model="inviteUsesMode" class="mb-2">
              <el-radio value="limited">次数限制</el-radio>
              <el-radio value="unlimited">无限使用</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="inviteUsesMode === 'limited'" label="次数">
            <el-input-number v-model="inviteUses" :min="1" :max="1000" />
          </el-form-item>

          <el-form-item label="备注 (可选)">
            <el-input
              v-model="inviteNote"
              type="textarea"
              :rows="2"
              placeholder="填写该邀请码的用途..."
            />
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <div class="px-6 pb-6">
          <el-button @click="inviteDialogVisible = false">取消</el-button>
          <el-button
            type="primary"
            @click="confirmCreateInvite"
            :loading="creating"
            class="hover-lift"
            >确认创建</el-button
          >
        </div>
      </template>
    </UiDialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Refresh, Plus, Delete, Ticket } from '@element-plus/icons-vue'
import CursorPager from '@/components/common/CursorPager.vue'
import { useCursorPagination } from '@/composables/useCursorPagination'
import { getAdminInvites, createAdminInvite, deleteAdminInvite } from '@/api/admin/invites'
import type { Invite } from '@/api/types'
import PageHeader from '@/components/common/PageHeader.vue'
import UiCard from '@/components/ui/UiCard.vue'
import UiDialog from '@/components/ui/UiDialog.vue'
import UiSegmented from '@/components/ui/UiSegmented.vue'
import { getErrorMessage } from '@/utils/error'

const invites = ref<Invite[]>([])
const limit = 15
const pagination = useCursorPagination<Invite>(limit)
const inviteDialogVisible = ref(false)
const inviteMode = ref('auto')
const customInviteCode = ref('')
const previewInviteCode = ref('')
const creating = ref(false)
const inviteUsesMode = ref('limited')
const inviteUses = ref(1)
const inviteNote = ref('')

function formatDate(ts: number | undefined) {
  return ts
    ? new Date(ts).toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-'
}

async function loadInvites() {
  try {
    const res = await getAdminInvites({
      cursor: pagination.currentCursor.value,
      limit: limit,
    })
    invites.value = res.data.items
    pagination.setPageData(res.data)
  } catch {
    ElMessage.error('加载列表失败')
  }
}

async function handleNextPage() {
  await pagination.goToNextPage(async (cursor, pageLimit) => {
    const res = await getAdminInvites({ cursor, limit: pageLimit })
    invites.value = res.data.items
    return res.data
  })
}

async function handlePrevPage() {
  await pagination.goToPrevPage(async (cursor, pageLimit) => {
    const res = await getAdminInvites({ cursor, limit: pageLimit })
    invites.value = res.data.items
    return res.data
  })
}

async function refreshFirstPage() {
  pagination.reset()
  await loadInvites()
}

function generateRandomCode() {
  return (
    Math.random().toString(36).substring(2, 10).toUpperCase() +
    Math.random().toString(36).substring(2, 10).toUpperCase()
  )
}

function showInviteDialog() {
  inviteMode.value = 'auto'
  customInviteCode.value = ''
  previewInviteCode.value = generateRandomCode()
  inviteUsesMode.value = 'limited'
  inviteUses.value = 1
  inviteNote.value = ''
  inviteDialogVisible.value = true
}

function refreshPreview() {
  previewInviteCode.value = generateRandomCode()
}

const getRemainingBg = (row: Invite) => {
  if (!row.total_uses) return 'rgba(103, 194, 58, 0.1)'
  const rem = row.total_uses - (row.used_count || 0)
  if (rem <= 0) return 'rgba(245, 108, 108, 0.1)'
  if (rem <= row.total_uses * 0.2) return 'rgba(230, 162, 60, 0.1)'
  return 'rgba(64, 158, 255, 0.1)'
}

const getRemainingColor = (row: Invite) => {
  if (!row.total_uses) return 'var(--el-color-success)'
  const rem = row.total_uses - (row.used_count || 0)
  if (rem <= 0) return 'var(--el-color-danger)'
  if (rem <= row.total_uses * 0.2) return 'var(--el-color-warning)'
  return 'var(--el-color-primary)'
}

async function confirmCreateInvite() {
  const code = inviteMode.value === 'auto' ? previewInviteCode.value : customInviteCode.value
  if (code.length === 0) return ElMessage.warning('请输入邀请码')

  creating.value = true
  try {
    const payload = {
      code,
      note: inviteNote.value,
      total_uses: inviteUsesMode.value === 'unlimited' ? null : inviteUses.value,
    }
    await createAdminInvite(payload)
    ElMessage.success('创建成功')
    inviteDialogVisible.value = false
    await refreshFirstPage()
  } catch (e: unknown) {
    ElMessage.error(getErrorMessage(e, '创建失败'))
  } finally {
    creating.value = false
  }
}

async function deleteInvite(invite: Invite) {
  try {
    await ElMessageBox.confirm('确定删除该邀请码吗？', '确认', { type: 'warning' })
    await deleteAdminInvite(invite.code)
    ElMessage.success('已删除')
    await refreshFirstPage()
  } catch {}
}

onMounted(refreshFirstPage)
</script>

<style scoped>
.code-text {
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-weight: 600;
  color: var(--color-heading);
}
.usage-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}
.note-text {
  color: var(--color-text-light);
  font-size: 13px;
}
.time-text {
  color: var(--color-text-light);
  font-size: 12px;
}
</style>
